import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatbotAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async (days = 30) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai/analytics?days=${days}`, { withCredentials: true });
      setData(res.data);
    } catch (err) { toast.error('Failed to load chatbot analytics'); }
    setLoading(false);
  };

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">AI Chatbot Analytics</h2>
            <p className="text-sm text-slate-500">Track chatbot usage, popular questions, and engagement</p>
          </div>
          <Button size="sm" onClick={() => fetchData(30)} disabled={loading}>
            {loading ? 'Loading...' : 'Load Analytics'}
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-4xl mb-3">🤖</div>
            <p className="text-slate-500 text-sm">Click "Load Analytics" to see chatbot usage data</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const maxTopic = Math.max(...Object.values(data.popular_topics || {}), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">AI Chatbot Analytics</h2>
          <p className="text-sm text-slate-500">Last {data.period_days} days</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => fetchData(30)} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Messages', value: data.summary.total_messages, color: 'text-slate-900' },
          { label: 'Public Chat', value: data.summary.public_messages, color: 'text-emerald-600' },
          { label: 'Wizard Chat', value: data.summary.wizard_messages, color: 'text-blue-600' },
          { label: 'Public Sessions', value: data.summary.public_sessions, color: 'text-purple-600' },
          { label: 'Avg Msgs/Session', value: data.summary.avg_messages_per_session, color: 'text-amber-600' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-4">
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Popular Topics</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(data.popular_topics || {}).map(([topic, count]) => (
              <div key={topic} className="flex items-center gap-3">
                <span className="text-sm text-slate-600 w-48 truncate capitalize">{topic.replace(/_/g, ' ')}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-4">
                  <div className="bg-emerald-500 h-4 rounded-full" style={{ width: `${(count / maxTopic) * 100}%` }}></div>
                </div>
                <span className="text-sm font-medium w-10 text-right">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {data.daily_volume?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Daily Volume</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {data.daily_volume.slice(-30).map((d, i) => {
                const maxV = Math.max(...data.daily_volume.map(v => v.messages), 1);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center" title={`${d.date}: ${d.messages}`}>
                    <div className="w-full bg-emerald-500 rounded-t hover:bg-emerald-600" style={{ height: `${(d.messages / maxV) * 100}%`, minHeight: d.messages > 0 ? '4px' : '0' }}></div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Recent Questions</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {data.recent_questions?.map((q, i) => (
              <div key={i} className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                <span className={`text-xs px-2 py-0.5 rounded-full ${q.source === 'public' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {q.source}
                </span>
                <p className="text-sm text-slate-700 flex-1">{q.question}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
